import { useCallback, useEffect, useMemo, useState } from "react"
import {
    AppDetailBase,
    AppDetailBaseProps
} from "../AppDetailBase/AppDetailBase"
import {
    canSubmit,
    InputRenderEntry
} from "../../../../../../Reducers/Client/InputRender/InputRenderReducer"
import { Loader, Button, Text } from "dash-styled"
import { useAsyncCommand } from "../../../../../../Hooks/useAsyncCommand"
import { createJobs } from "../../../../../../Actions/API/Job/JobActions"
import { dirtinessChanged } from "../../../../../../Actions/Client/InputRender/InputRenderActions"
import {
    Render,
    InputRenderData
} from "../../../../../Common/Render/Variants/JobInput/Common/Render/Render"
import { InputMode } from "../../../../../Common/Render/Variants/JobInput/Common/Container/Container"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import { nameToSlug } from "../../../../../../Models/API/AppTemplateModel"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { AdvancedSettings } from "./AdvancedSettings"
import { getCount } from "../../../../../../Models/Client/JobSubmitData"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { UploadStatus } from "../../../../../../Models/Client/UploadQueueItem"
import { useDispatch } from "react-redux"
import { hasValue } from "../../../../../../Models/Client/FetchState"
import { JobSubmitData } from "../../../../../../Models/Client/JobSubmitData"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useJobCount } from "../../../../../../Hooks/useJobCount"
import { APIJobsPerStatusModel } from "../../../../../../Models/API/APIJobsPerStatusModel"
import {
    FREEMIUM_JOB_LIMIT,
    useCanUploadJobs
} from "../../../../../../Hooks/useCanUploadJobs"
import { LinkButton } from "../../../../../Common/LinkButton/LinkButton"
import { useAppSchema } from "../../../../../../Hooks/useAppSchema"

const ITEM_FORM_NAME = (appID: string) => `${appID}_add_item`

interface AddDataProps {
    orgUsername: string
    appID: string
    mode: InputMode
}

export const AddData = ({ orgUsername, appID, mode }: AddDataProps) => {
    const dispatch = useDispatch()
    const formID = ITEM_FORM_NAME(appID)
    const [clearDirtiness, setClearDirtiness] = useState(false)
    const changeMode = useCallback(
        (button: InputMode) =>
            dispatch(push(routePaths.appAddData(orgUsername, appID, button))),
        [dispatch, appID, orgUsername]
    )
    const [inputs, setInputs] = useState<InputRenderData | undefined>()

    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const [initiateSubmit, startSubmit, stopSubmit] = useBoolean(false)
    const [queue, , , toggleQueue] = useBoolean(true)
    const jobCounts = useJobCount(appID)
    const { upload_allowed, remaining_count } = useCanUploadJobs(appID)
    const [tags, setTags] = useState<string[]>([])
    const [strict, setStrict] = useState<boolean>(false)
    const templateFetch = useAppTemplate(appID)
    const schemaFetch = useAppSchema(appID, false)
    const totalJobCountBeforeSubmit =
        jobCounts === undefined
            ? undefined
            : APIJobsPerStatusModel.total(jobCounts)
    const handleDirtinessChange = useCallback(
        (isDirty: boolean) => dispatch(dirtinessChanged(isDirty)),
        [dispatch]
    )

    const handleSubmit = useCallback(
        async (data: InputRenderEntry, jobCount: number) => {
            try {
                await dispatch(
                    createJobs(appID, data.values as JobSubmitData, jobCount, {
                        strict,
                        queue,
                        tags
                    })
                )

                // clear dirtiness once the action is complete
                handleDirtinessChange(false)

                setClearDirtiness(true) // this helps in clearing the dirtiness on component unmount, since in certain cases the data is updated with undefined after submit
                dispatch(push(routePaths.appDetail(orgUsername, appID)))
            } finally {
                stopSubmit()
            }
        },
        [
            dispatch,
            appID,
            orgUsername,
            handleDirtinessChange,
            setClearDirtiness,
            queue,
            strict,
            tags,
            stopSubmit
        ]
    )

    const submit = useMemo(() => {
        if (inputs === undefined || totalJobCountBeforeSubmit === undefined) {
            return undefined
        }

        return async () => handleSubmit(inputs, totalJobCountBeforeSubmit)
    }, [inputs, handleSubmit, totalJobCountBeforeSubmit])

    useEffect(
        () => () => {
            clearDirtiness && handleDirtinessChange(false)
        },
        [clearDirtiness, handleDirtinessChange]
    )
    const { isProcessing, perform } = useAsyncCommand(submit)
    const uploaderStateKey = `${formID}-instance-input-upload`
    const itemCount =
        inputs === undefined
            ? undefined
            : getCount(inputs.values as JobSubmitData)
    const hasOngoingUpload = useAppState(s =>
        s.client.interface.uploader[uploaderStateKey]?.some(
            uploadState =>
                uploadState.status === UploadStatus.InProgress ||
                uploadState.status === UploadStatus.Queued
        )
    )

    useEffect(() => {
        // for `input` mode, an initiate submit is triggered and when input is ready to be submit, canSubmit is set to true which triggers the submit
        if (inputs?.canSubmit === true) {
            perform?.()
        }
    }, [inputs, perform])

    const containerProps: AppDetailBaseProps = {
        orgUsername,
        id: appID,
        title: "Add data",
        breadcrumbs: [],
        fixedContentWidth: true,
        forceUserExpiryContact: userExpiryContact,
        resetUserExpiryContact: hideUserExpiryContact
    }

    if (!hasValue(templateFetch) || !hasValue(schemaFetch)) {
        return (
            <AppDetailBase {...containerProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    const template = templateFetch.value

    return (
        <AppDetailBase {...containerProps}>
            <div className="my-6">
                <Render
                    {...schemaFetch.value.input}
                    orgUsername={orgUsername}
                    appID={appID}
                    id={formID}
                    onChangeMode={changeMode}
                    activeMode={mode}
                    contextName={nameToSlug(template.name)}
                    uploaderStateKey={uploaderStateKey}
                    onDirtinessChange={handleDirtinessChange}
                    onInputChange={setInputs}
                    inputs={inputs}
                    initiateSubmit={initiateSubmit}
                    stopSubmit={stopSubmit}
                />
                {mode !== "sdk" && (
                    <AdvancedSettings
                        appID={appID}
                        className="max-w-4xl mx-auto mt-4"
                        queueOnSubmit={queue}
                        tags={tags}
                        strict={strict}
                        onChangeQueueOnSubmit={toggleQueue}
                        onChangeTags={setTags}
                        onChangeStrict={setStrict}
                    />
                )}
                <div className="flex flex-col items-center justify-center pt-4">
                    {mode !== "sdk" && (
                        <>
                            {remaining_count !== undefined &&
                                inputs !== undefined &&
                                remaining_count - (itemCount ?? 0) < 0 && (
                                    <Text className="mb-2 text-coral-700">
                                        {`You are exceeding your datapoints limit of ${FREEMIUM_JOB_LIMIT}. Can upload ${remaining_count} more data. `}
                                        <LinkButton
                                            onClick={showUserExpiryContact}
                                        >
                                            Contact us
                                        </LinkButton>
                                    </Text>
                                )}
                            <Button
                                onClick={
                                    mode === "input" ? startSubmit : perform
                                }
                                disabled={
                                    !canSubmit(inputs) ||
                                    isProcessing ||
                                    hasOngoingUpload ||
                                    !upload_allowed ||
                                    initiateSubmit
                                }
                                className="w-56"
                                testId="add-data-submit-btn"
                            >
                                {isProcessing
                                    ? `Submitting ${itemCount ?? ""}...`
                                    : `Submit ${
                                          itemCount ?? ""
                                      } for processing`}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </AppDetailBase>
    )
}
